import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, Globe, Clock, MapPin, Tag } from 'lucide-react';
import { NewsItem } from '../types';

interface NewsDetailOverlayProps {
    newsItem: NewsItem | null;
    onClose: () => void;
}

const NewsDetailOverlay: React.FC<NewsDetailOverlayProps> = ({ newsItem, onClose }) => {
    if (!newsItem) return null;

    const [isAskingAi, setIsAskingAi] = React.useState(false);

    const shareNews = () => {
        if (navigator.share) {
            navigator.share({
                title: newsItem.headline,
                text: newsItem.summary || newsItem.description,
                url: window.location.href
            });
        }
    };

    const handleAskAi = () => {
        setIsAskingAi(true);
        // Simulate AI interaction
        setTimeout(() => setIsAskingAi(false), 2000);
    };

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-[110] flex items-end md:items-center justify-center pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto" onClick={onClose} />

                <motion.div
                    className="relative pointer-events-auto bg-[#0F172A] border border-blue-500/20 w-full md:w-[600px] h-[85vh] md:h-auto md:max-h-[85vh] md:rounded-2xl rounded-t-3xl shadow-2xl flex flex-col overflow-hidden"
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "100%" }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                >
                    {/* 1. HEADER: Author & Meta */}
                    <div className="flex items-center justify-between p-4 border-b border-white/5 bg-[#0b1021]">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center border border-blue-500/30">
                                <Globe size={20} className="text-blue-400" />
                            </div>
                            <div>
                                <h3 className="text-white font-bold text-sm leading-tight">
                                    {newsItem.author || newsItem.source}
                                </h3>
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                    <span className="text-blue-400 font-medium">@{newsItem.source.replace(/\s+/g, '').toLowerCase()}</span>
                                    <span>•</span>
                                    <span>{newsItem.time}</span>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 -mr-2 text-slate-400 hover:text-white transition-colors"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    <div className="overflow-y-auto custom-scrollbar flex-1 bg-[#0F172A]">
                        {/* 2. MEDIA CONTENT */}
                        <div className="relative aspect-video bg-indigo-900/20 w-full overflow-hidden">
                            <img
                                src={newsItem.imageUrl || `https://source.unsplash.com/800x600/?${newsItem.category.toLowerCase()},news`}
                                className="w-full h-full object-cover"
                                alt="News Cover"
                                onError={(e) => (e.currentTarget.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80')}
                            />
                            {/* GPS HUD Overlay */}
                            {newsItem.coordinates && (
                                <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur px-3 py-1.5 rounded-lg border border-white/10 flex items-center gap-2 text-[10px] font-mono text-blue-400 tracking-wider">
                                    <MapPin size={12} />
                                    <span>
                                        {newsItem.coordinates.lat.toFixed(5)} N, {newsItem.coordinates.lng.toFixed(5)} E
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* 3. BODY TEXT */}
                        <div className="p-5">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/10 text-blue-400 border border-blue-500/20 uppercase tracking-wider">
                                    {newsItem.category}
                                </span>
                                <div className="h-1 w-1 rounded-full bg-slate-600" />
                                <span className="text-xs text-slate-400 font-mono">
                                    REALITY SCORE: {newsItem.realityScore}/10
                                </span>
                            </div>

                            <h2 className="text-xl font-bold text-white mb-3">
                                {newsItem.headline}
                            </h2>

                            <p className="text-slate-300 text-sm leading-relaxed mb-6">
                                {newsItem.description}
                                {newsItem.summary && (
                                    <span className="block mt-2 text-slate-400 italic">
                                        "{newsItem.summary}"
                                    </span>
                                )}
                            </p>

                            {/* 4. TACTICAL ADVICE (WHAT TO DO) */}
                            {newsItem.advice && newsItem.advice.length > 0 && (
                                <div className="mb-6 bg-blue-900/10 border border-blue-500/20 rounded-xl overflow-hidden">
                                    <div className="bg-blue-500/10 px-4 py-2 border-b border-blue-500/10 flex items-center justify-between">
                                        <h4 className="text-xs font-bold text-blue-400 uppercase tracking-widest flex items-center gap-2">
                                            <Tag size={12} /> TACTICAL ADVICE
                                        </h4>
                                    </div>
                                    <div className="p-4">
                                        <ul className="space-y-3">
                                            {newsItem.advice.map((tip, idx) => (
                                                <li key={idx} className="flex items-start gap-3 text-sm text-slate-300">
                                                    <div className="mt-1 min-w-[16px] h-4 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-[10px] font-bold uppercase">
                                                        {idx + 1}
                                                    </div>
                                                    <span>{tip}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 5. FOOTER ACTIONS */}
                    <div className="p-4 border-t border-white/5 bg-[#0b1021] flex items-center gap-3">
                        <button
                            onClick={handleAskAi}
                            disabled={isAskingAi}
                            className="flex-1 py-3 bg-[var(--neon-cyan)] hover:brightness-110 text-black rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2"
                        >
                            {isAskingAi ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                                    Analyzing...
                                </>
                            ) : (
                                <>
                                    <Tag size={16} />
                                    Ask AI Assistant
                                </>
                            )}
                        </button>
                        <button onClick={shareNews} className="p-3 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors">
                            <Share2 size={20} />
                        </button>
                    </div>

                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default NewsDetailOverlay;
